import {
  QueryClient,
  QueryKey,
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';
import { QueryOptionsConfig } from 'api/hooks';
import {
  ArchivedProfileResponse,
  EventSummaryResponse,
  LearnerAndInviteeResponse,
  LearnerWithEvaluation,
  MutationResponse,
  OrganisationResponse,
  PractitionerSummaryData,
  ProfileResponse,
  RequestError,
  SupportedRolesResponse,
  TeamResponse,
  UserInvite,
} from 'api/httpEntities';
import { QUERY_KEY_ROOTS } from 'variables';
import {
  createMultiOrgTeam,
  CreateMultiOrgTeamRequest,
  getLearnerByEmail,
  getOrgsWithRevokeAccessFlag,
  mergeLearners,
  purchaseProfilesForTeam,
} from 'api/api';
import {
  CreateEventData,
  InviteUserData,
  MergeLearnersRequest,
  OptOutStatusResponse,
  OrganisationEditRequest,
  OrganisationMoveRequest,
  OrganisationRequest,
  PurchaseLearnerRequest,
  TeamRequest,
} from './organisationApitypes';
import {
  addOrganisation,
  addTeam,
  addToExistingTeam,
  createEvent,
  createNewEvaluatorLink,
  deleteOrganisation,
  getAllOrganisations,
  getArchivedProfilesByOrganisation,
  getEvaluationsProfilesByOrg,
  getEvaluationsProfilesByOrgs,
  getLearnerByOrganisation,
  getOpenPractitionerInvites,
  getOrganisation,
  getOrganisationEvents,
  getOrganisationOptOutStatus,
  getPractitionersSummary,
  getProfilesByOrganisation,
  getSupportedRoles,
  inviteUser,
  moveOrganisation,
  purchaseLearnerChapters,
  purchaseProfilesForEvaluatorLink,
  setPreferredPronounAndDialect,
  updateOrganisation,
  updateOrganisationOptOutStatus,
} from './organisations';

export const useGetPractitionersSummary = (
  organisationId: string,
  config: QueryOptionsConfig<PractitionerSummaryData[]> = {},
): UseQueryResult<PractitionerSummaryData[], Error> =>
  useQuery<PractitionerSummaryData[], Error>({
    queryKey: [QUERY_KEY_ROOTS.PRACTITIONERS_SUMMARY_DATA, organisationId],
    queryFn: async () => getPractitionersSummary(organisationId),
    ...config,
  });

export const useCreateEvent = (): UseMutationResult<
  MutationResponse,
  unknown,
  CreateEventData,
  unknown
> =>
  useMutation({
    mutationFn: createEvent,
  });

export const useGetOrgEvents = (
  organisationId: string,
  status: string,
  config: QueryOptionsConfig<EventSummaryResponse[]> = {},
): UseQueryResult<EventSummaryResponse[], Error> =>
  useQuery<EventSummaryResponse[], Error>({
    queryKey: [QUERY_KEY_ROOTS.ORGANISATION_EVENT_DATA, organisationId, status],
    queryFn: async () => getOrganisationEvents(organisationId, status),
    ...config,
  });

export const useInviteUser = (
  organisationId: string,
  queryClient: QueryClient,
): UseMutationResult<Response, unknown, InviteUserData, unknown> =>
  useMutation({
    mutationFn: inviteUser,
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [
          QUERY_KEY_ROOTS.OPEN_PRACTITIONER_INVITES_DATA,
          organisationId,
        ],
      }),
  });

export const useAddOrganisation = (
  queryClient: QueryClient,
): UseMutationResult<MutationResponse, unknown, OrganisationRequest, unknown> =>
  useMutation({
    mutationFn: addOrganisation,
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_ROOTS.ORGANISATIONS_DATA],
      }),
  });

export const useGetAllOrganisations = (
  config: QueryOptionsConfig<OrganisationResponse[]> = {},
): UseQueryResult<OrganisationResponse[], Error> =>
  useQuery({
    queryKey: [QUERY_KEY_ROOTS.ORGANISATIONS_DATA] as QueryKey,
    queryFn: async () => getAllOrganisations(),
    ...config,
  });

export const useGetOrganisation = (
  organisationId?: string,
  config?: { enabled: boolean },
): UseQueryResult<OrganisationResponse, Error> =>
  useQuery({
    queryKey: [QUERY_KEY_ROOTS.ORGANISATION_DATA, organisationId],
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    queryFn: async () => getOrganisation(organisationId!),
    ...{ enabled: !!organisationId, ...config },
  });

export const useGetOrganisationOptOutStatus = (
  organisationId?: string,
  config?: { enabled: boolean },
): UseQueryResult<boolean, Error> =>
  useQuery({
    queryKey: [
      QUERY_KEY_ROOTS.ORGANISATION_GEN_AI_OPT_OUT_STATUS,
      organisationId,
    ],
    queryFn: async () => {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const { optedOut } = await getOrganisationOptOutStatus(organisationId!);
      return optedOut;
    },
    retry: false,
    ...{ enabled: !!organisationId, ...config },
  });

export const useUpdateOrganisationOptOutStatus = (
  queryClient: QueryClient,
): UseMutationResult<
  OptOutStatusResponse,
  RequestError,
  { organisationId: string; optedOut: boolean }
> =>
  useMutation({
    mutationFn: updateOrganisationOptOutStatus,
    onSuccess: (_, { organisationId }) =>
      queryClient.invalidateQueries({
        queryKey: [
          QUERY_KEY_ROOTS.ORGANISATION_GEN_AI_OPT_OUT_STATUS,
          organisationId,
        ],
      }),
  });

export const useGetSupportedRoles = (
  organisationId?: string,
  config?: { enabled: boolean },
): UseQueryResult<SupportedRolesResponse, Error> =>
  useQuery({
    queryKey: [
      QUERY_KEY_ROOTS.ORGANISATION_SUPPORTED_ROLES_DATA,
      organisationId,
    ],
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    queryFn: async () => getSupportedRoles(organisationId!),
    ...{ enabled: !!organisationId, ...config },
  });

export const useUpdateOrganisation = (
  organisationId: string,
  queryClient: QueryClient,
): UseMutationResult<MutationResponse, RequestError, OrganisationEditRequest> =>
  useMutation<MutationResponse, RequestError, OrganisationEditRequest>({
    mutationFn: updateOrganisation,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_ROOTS.ORGANISATIONS_DATA],
      });
      return queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_ROOTS.ORGANISATION_DATA, organisationId],
      });
    },
  });

export const useMoveLearnerOrganisation = (
  organisationId: string,
  queryClient: QueryClient,
): UseMutationResult<void, RequestError, OrganisationMoveRequest, unknown> =>
  useMutation({
    mutationFn: moveOrganisation,
    onSuccess: () => {
      queryClient
        .invalidateQueries({
          queryKey: [QUERY_KEY_ROOTS.ORGANISATION_DATA, organisationId],
        })
        .then();
      queryClient
        .invalidateQueries({ queryKey: [QUERY_KEY_ROOTS.EVENTS_DATA] })
        .then();
      queryClient
        .invalidateQueries({ queryKey: [QUERY_KEY_ROOTS.LEARNER_TEAM_DATA] })
        .then();
    },
  });

export const useMergeLearners = (
  queryClient: QueryClient,
): UseMutationResult<void, RequestError, MergeLearnersRequest> =>
  useMutation({
    mutationFn: mergeLearners,
    onSuccess: (
      _data,
      { organisationId, sourceLearnerId, targetLearnerId },
    ) => {
      queryClient.removeQueries({
        queryKey: [
          QUERY_KEY_ROOTS.LEARNER_DATA,
          organisationId,
          sourceLearnerId,
        ],
      });
      [
        [QUERY_KEY_ROOTS.ORGANISATION_DATA, organisationId],
        [QUERY_KEY_ROOTS.LEARNERS_DATA],
        [QUERY_KEY_ROOTS.LEARNER_DATA, organisationId, targetLearnerId],
        [QUERY_KEY_ROOTS.PROFILES_DATA, targetLearnerId, organisationId],
        [
          QUERY_KEY_ROOTS.ARCHIVED_PROFILES_DATA,
          targetLearnerId,
          organisationId,
        ],
        [QUERY_KEY_ROOTS.LEARNER_TEAM_DATA, targetLearnerId],
        [QUERY_KEY_ROOTS.EVENTS_DATA, targetLearnerId],
      ].forEach((queryKey) => {
        queryClient.invalidateQueries({ queryKey }).then();
      });
    },
  });

export const useDeleteOrganisation = (
  queryClient: QueryClient,
): UseMutationResult<void, RequestError, string, unknown> =>
  useMutation({
    mutationFn: deleteOrganisation,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_ROOTS.ORGANISATION_DATA],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_ROOTS.ORGANISATIONS_DATA],
      });
    },
  });

export const useGetLearnerByOrganisation = (
  learnerId: string,
  organisationId: string,
  config?: { enabled: boolean },
): UseQueryResult<LearnerWithEvaluation, RequestError> =>
  useQuery({
    queryKey: [QUERY_KEY_ROOTS.LEARNER_DATA, organisationId, learnerId],
    queryFn: async () => getLearnerByOrganisation(learnerId, organisationId),
    ...config,
  });

export const useGetLearnerByEmail = (
  organisationId: string,
  email: string,
): UseQueryResult<LearnerAndInviteeResponse, Error> =>
  useQuery({
    queryKey: [QUERY_KEY_ROOTS.LEARNER_BY_EMAIL_DATA, organisationId, email],
    queryFn: async () => getLearnerByEmail(organisationId, email),
    enabled: !!email,
  });

export const useGetProfilesByOrganisation = (
  learnerId: string,
  organisationId: string,
): UseQueryResult<ProfileResponse[], RequestError> =>
  useQuery({
    queryKey: [QUERY_KEY_ROOTS.PROFILES_DATA, learnerId, organisationId],
    queryFn: async () => {
      const { profiles } = await getProfilesByOrganisation(
        learnerId,
        organisationId,
      );
      return profiles;
    },
  });

export const useGetArchivedProfilesByOrganisation = (
  learnerId: string,
  organisationId: string,
): UseQueryResult<ArchivedProfileResponse[], RequestError> =>
  useQuery({
    queryKey: [
      QUERY_KEY_ROOTS.ARCHIVED_PROFILES_DATA,
      learnerId,
      organisationId,
    ],
    queryFn: async () => {
      const { profiles: archivedProfiles } =
        await getArchivedProfilesByOrganisation(learnerId, organisationId);
      return archivedProfiles;
    },
  });

export const usePurchaseLearnerChapter = (
  queryClient: QueryClient,
): UseMutationResult<any, RequestError, PurchaseLearnerRequest, unknown> =>
  useMutation({
    mutationFn: purchaseLearnerChapters,
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_ROOTS.LEARNER_DATA],
        }),
        queryClient.invalidateQueries({
          queryKey: [QUERY_KEY_ROOTS.PROFILES_DATA],
        }),
      ]),
  });

export const useGetOpenPractitionerInvites = (
  organisationId: string,
  config: QueryOptionsConfig<UserInvite[]> = {},
): UseQueryResult<UserInvite[], Error> =>
  useQuery<UserInvite[], Error>({
    queryKey: [QUERY_KEY_ROOTS.OPEN_PRACTITIONER_INVITES_DATA, organisationId],
    queryFn: async () => {
      const { invites } = await getOpenPractitionerInvites(organisationId);
      return invites;
    },
    ...config,
  });

export const useAddTeam = (
  queryClient: QueryClient,
): UseMutationResult<
  TeamResponse,
  unknown,
  { organisationId: string; formData: TeamRequest },
  unknown
> =>
  useMutation({
    mutationFn: addTeam,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_ROOTS.TEAMS_DATA] }),
  });

export const useAddToExistingTeam = (
  queryClient: QueryClient,
): UseMutationResult<
  { id: string },
  unknown,
  { teamId: string; learnerIds: string[] },
  unknown
> =>
  useMutation({
    mutationFn: addToExistingTeam,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_ROOTS.TEAMS_DATA] }),
  });

export const useAddMultiOrgTeam = (
  queryClient: QueryClient,
): UseMutationResult<
  TeamResponse,
  unknown,
  CreateMultiOrgTeamRequest,
  unknown
> => {
  return useMutation({
    mutationFn: createMultiOrgTeam,
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_ROOTS.TEAMS_DATA] }),
  });
};
export const usePurchaseProfilesForEvaluatorLink = (queryClient: QueryClient) =>
  useMutation({
    mutationFn: purchaseProfilesForEvaluatorLink,
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_ROOTS.PROFILES_DATA],
      }),
  });

export const useCreateNewEvaluatorLink = (queryClient: QueryClient) =>
  useMutation({
    mutationFn: createNewEvaluatorLink,
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_ROOTS.ORGANISATION_EVALUATOR_LINKS],
      }),
  });

export const useGetEvaluationsByOrg = (
  organisationId: string,
  evaluationIds: string[],
) =>
  useQuery({
    queryKey: [
      QUERY_KEY_ROOTS.EVALUATOR_PROFILES,
      organisationId,
      evaluationIds.join(),
      evaluationIds,
    ],
    queryFn: async () => {
      const { evaluations } = await getEvaluationsProfilesByOrg({
        organisationId,
        evaluationIds,
      });
      return evaluations;
    },
  });

export const useGetEvaluationsByOrgs = (
  searches: { organisationId: string; evaluationIds: string[] }[],
) =>
  useQuery({
    queryKey: [QUERY_KEY_ROOTS.EVALUATOR_PROFILES, searches],
    queryFn: async () => {
      const { evaluations } = await getEvaluationsProfilesByOrgs(searches);
      return evaluations;
    },
  });

export const useGetOrgsWithRevokeAccessFlag = (
  practitionerId: string,
  config: QueryOptionsConfig<OrganisationResponse[]>,
) => {
  return useQuery({
    queryKey: [
      QUERY_KEY_ROOTS.PRACTITIONER_DATA,
      practitionerId,
      'organisationsWithRevoke',
    ] as QueryKey,
    queryFn: async () => await getOrgsWithRevokeAccessFlag(practitionerId),
    ...config,
  });
};

export const usePurchaseProfilesForTeam = (queryClient: QueryClient) =>
  useMutation({
    mutationFn: purchaseProfilesForTeam,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_ROOTS.PROFILES_DATA],
      });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY_ROOTS.TEAM_DATA] });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEY_ROOTS.TEAM_LEARNERS_DATA],
      });
    },
  });

export const useSetPronounsAndPreferredDialect = (
  queryClient: QueryClient,
  onSuccess: () => void,
) =>
  useMutation({
    mutationFn: setPreferredPronounAndDialect,
    onSuccess: () => {
      queryClient
        .invalidateQueries({
          queryKey: [QUERY_KEY_ROOTS.LEARNER_DATA],
        })
        .then();
      queryClient
        .invalidateQueries({ queryKey: [QUERY_KEY_ROOTS.PROFILES_DATA] })
        .then();
      onSuccess();
    },
  });
